/* eslint-disable no-magic-numbers */

import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  Button,
  CopyToClipboard,
  DescriptionList,
  type DescriptionListItem,
  Inline,
  Modal,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useMemo } from 'react';

import { WrappedMixpanel } from '../../../mixpanelWrapper';
import { useBankDetails } from '../hooks';

const CONTACT_DETAILS = {
  REMITTANCES_EMAIL: 'remittances@omnipresent.com',
  CUSTOMER_INVOICES_EMAIL: 'help@omnipresent.com',
} as const;

const MODAL_TITLE = 'Omnipresent bank details';
const MODAL_DESCRIPTION =
  'Invoices can be paid through one of the 3 accounts below.';

const ListItem = ({
  value,
  mixpanelEventAttributes,
}: {
  value: string;
  mixpanelEventAttributes?: Record<string, string>;
}) => (
  <Inline justify="space-between" align="center">
    <Typography size="14" as="span">
      {value}
    </Typography>

    <CopyToClipboard
      textToCopy={value}
      onClick={() =>
        mixpanelEventAttributes &&
        WrappedMixpanel.track(value, mixpanelEventAttributes)
      }
    />
  </Inline>
);

type Props = {
  open: boolean;
  closeFn: () => void;
};

export const BankDetailsModal = (props: Props) => {
  const { open, closeFn } = props;

  const [internationalPayments, domesticGBPPayments, domesticUSDPayments] =
    useBankDetails();

  const internationalPaymentsDetails: DescriptionListItem[] = useMemo(
    () =>
      internationalPayments.map(({ title, value }) => ({
        header: title,
        detail: (
          <ListItem
            value={value}
            mixpanelEventAttributes={{
              paymentLocation: 'International',
              bankDetail: title,
            }}
          />
        ),
      })),
    [internationalPayments],
  );

  const domesticGBPPaymentsDetails: DescriptionListItem[] = useMemo(
    () =>
      domesticGBPPayments.map(({ title, value }) => ({
        header: title,
        detail: (
          <ListItem
            value={value}
            mixpanelEventAttributes={{
              paymentLocation: 'Domestic GBP',
              bankDetail: title,
            }}
          />
        ),
      })),
    [domesticGBPPayments],
  );

  const domesticUSDPaymentsDetails: DescriptionListItem[] = useMemo(
    () =>
      domesticUSDPayments.map(({ title, value }) => ({
        header: title,
        detail: (
          <ListItem
            value={value}
            mixpanelEventAttributes={{
              paymentLocation: 'Domestic US',
              bankDetail: title,
            }}
          />
        ),
      })),
    [domesticUSDPayments],
  );

  return (
    <Modal
      open={open}
      span={6}
      onOpenChange={() => {
        closeFn();
      }}
    >
      <Modal.Header title={MODAL_TITLE} />

      <Modal.Body>
        <Stack gap="24">
          <Typography as="span" size="16">
            {MODAL_DESCRIPTION}
          </Typography>

          <AccordionRoot type="multiple">
            <AccordionItem value="international-payments">
              <AccordionHeader>
                <AccordionTrigger>
                  <Inline
                    justify="space-between"
                    w="stretch"
                    align="center"
                    pt="4"
                    pb="4"
                  >
                    <Stack
                      align="self-start"
                      gap="0"
                      css={{ textAlign: 'start' }}
                    >
                      <Typography as="span" size="14" weight="medium">
                        International payments
                      </Typography>
                      <Typography as="span" size="14">
                        If your bank account is anywhere other than the United
                        Kingdom or the United States
                      </Typography>
                    </Stack>
                    <span role="img" aria-label="international-payments">
                      🌎
                    </span>
                  </Inline>
                </AccordionTrigger>
              </AccordionHeader>

              <AccordionContent>
                <DescriptionList
                  list={internationalPaymentsDetails}
                  id="international-payment-details"
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="domestic-gbp-payments">
              <AccordionHeader>
                <AccordionTrigger>
                  <Inline
                    justify="space-between"
                    w="stretch"
                    align="center"
                    pt="4"
                    pb="4"
                  >
                    <Stack align="self-start" gap="0">
                      <Typography as="span" size="14" weight="medium">
                        Domestic GBP payments
                      </Typography>
                      <Typography as="span" size="14">
                        If your bank account is in the United Kingdom
                      </Typography>
                    </Stack>
                    <span role="img" aria-label="domestic-gbp-payments">
                      🇬🇧
                    </span>
                  </Inline>
                </AccordionTrigger>
              </AccordionHeader>

              <AccordionContent>
                <DescriptionList
                  list={domesticGBPPaymentsDetails}
                  id="domestic-gbp-details"
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="domestic-usd-payments">
              <AccordionHeader>
                <AccordionTrigger>
                  <Inline
                    justify="space-between"
                    w="stretch"
                    align="center"
                    pt="4"
                    pb="4"
                  >
                    <Stack align="self-start" gap="0">
                      <Typography as="span" size="14" weight="medium">
                        Domestic USD payments
                      </Typography>
                      <Typography as="span" size="14">
                        If your bank account is in the United States
                      </Typography>
                    </Stack>
                    <span role="img" aria-label="domestic-usd-payments">
                      🇺🇸
                    </span>
                  </Inline>
                </AccordionTrigger>
              </AccordionHeader>

              <AccordionContent>
                <DescriptionList
                  list={domesticUSDPaymentsDetails}
                  id="domestic-usd-details"
                />
              </AccordionContent>
            </AccordionItem>
          </AccordionRoot>
          <DescriptionList
            list={[
              {
                header: 'Please email your remittance advice to',
                detail: <ListItem value={CONTACT_DETAILS.REMITTANCES_EMAIL} />,
              },
              {
                header: 'Need help?',
                detail: (
                  <ListItem value={CONTACT_DETAILS.CUSTOMER_INVOICES_EMAIL} />
                ),
              },
            ]}
            id="contact-info-details"
          />
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button variant="secondary" onClick={closeFn}>
            Cancel
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
